import { Component, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { AppInfoService } from 'core/app-info/app-info.service';
import { NotificationType } from 'core/notification/notification-config.class';
import { NotificationService } from 'core/notification/notification.service';
import { AppLocalization } from 'src/app/modules/shared/entities/app-localization';
import { TrackingInfo } from 'src/app/modules/crm/dashboard/entities/tracking-info';
import { ImportItemsQuoteService } from 'src/app/modules/shared/import-quotes-and-orders/import-items-quote.service';
import { DeviceDetectorService } from 'ngx-device-detector';
import { LnOrderHeader } from '../../entities/ln-order-header-entity';
import { OrderTrackingComponent } from '../../invoices/order-tracking/order-tracking.component';
import { LnOrdersService } from '../services/ln-orders.service';

@Component({
  selector: 'app-ln-order',
  templateUrl: './ln-order.component.html',
  styleUrls: ['./ln-order.component.scss'],
  standalone: false
})
export class LnOrderComponent implements OnInit {
  appStrings: AppLocalization;
  lnOrderNumber: string;
  lnOrderHeaderDetails: LnOrderHeader;
  customerId = '';
  leadId = '';
  private dialogWidth = '600px';
  isAccountDetails = true;
  loading = {
    orderHeader: false,
    trackingInformation: false
  };
  importQuoteActive = false;
  isMobile = false;
  breadcrumbs = [];

  constructor(
    private appInfoService: AppInfoService,
    private lnOrderService: LnOrdersService,
    private activatedRoute: ActivatedRoute,
    private router: Router,
    private dialog: MatDialog,
    private notificationService: NotificationService,
    private importItemsQuoteService: ImportItemsQuoteService,
    private deviceService: DeviceDetectorService
  ) { }

  ngOnInit() {
    this.isMobile = this.deviceService.isMobile();
    this.appStrings = this.appInfoService.appStrings;
    this.leadId = this.activatedRoute.snapshot.params['leadId'];
    this.lnOrderNumber = this.activatedRoute.snapshot.params['lnOrderNumber'];
    this.setCustomer();
    this.initData();
    this.importQuoteActive = !!this.importItemsQuoteService.getDestinationQuoteId();
    this.breadcrumbs = this.getBreadcrumbs();
  }

  /**
   * Getting order header data
   */
  initData() {
    this.loading.orderHeader = true;
    this.lnOrderService.getOrderHeader(this.lnOrderNumber,
      orderHeaderResult => {
        this.loading.orderHeader = false;
        this.lnOrderHeaderDetails = orderHeaderResult;
      });
  }

  /**
   * Setting customer based on route params
   */
  setCustomer() {
    const customerFromURL = this.activatedRoute.snapshot.params['customerId'];
    // When opened from customer DashboardComponent, we have the customer from URL
    if (customerFromURL === 'null') {
      this.isAccountDetails = false;
    } else {
      this.customerId = customerFromURL;
    }
  }

  /**
   * Closing LN Order View - redirecting to LN Orders list
   */
  close() {
    if (this.isAccountDetails) {
      this.goBack();
    } else {
      this.router.navigate(['/ln-orders']);
    }
  }

  /**
   * Closing LN Order View
   * Redirecting to Customer Dashboard
   */
  goBack() {
    this.router.navigate(['/accounts', this.customerId]);
  }

  /**
   * Prints LN Order
   * @param orderNumber LN order number
   */
  printLnOrder(orderNumber: string) {
    this.lnOrderService.printOrder(orderNumber);
  }

  /**
   * Tracks a LN Order by opening a tab with google search query
   */
  trackLnOrder() {
    if (!this.lnOrderHeaderDetails.orderNumber.trim().length) {
      this.notificationService.showNotification(this.appStrings.invoice_trackingNoOrderNumber, NotificationType.Error);
      return;
    }
    this.loading.trackingInformation = true;

    this.lnOrderService.getOrderTrackingNumberList(this.lnOrderHeaderDetails.orderNumber)
      .subscribe((orderTrackingList: TrackingInfo[]) => {
        this.loading.trackingInformation = false;
        this.displayTrackingOrdersDialog(orderTrackingList);
      });
  }

  /**
   * Opens order tracking popup with tracking info
   * @param orderTrackingList array of orders containing carrier and tracking number
   */
  private displayTrackingOrdersDialog(orderTrackingList: TrackingInfo[]) {
    this.dialog.open(OrderTrackingComponent, {
      width: this.dialogWidth,
      data: {
        orderTrackingList,
        orderNumber: this.lnOrderHeaderDetails.orderNumber
      }
    });
  }

  getBreadcrumbs(): any[] {
    const baseRoute = this.customerId ? '/accounts' : '/accounts-leads';
    const accountId = this.customerId || this.leadId;

    return [
      {
        label: this.appStrings.contacts_accountDetails,
        icon: this.isMobile ? 'arrow_back' : null,
        url: [baseRoute, accountId, { tab: 'ln-orders' }]
      },
      {
        label: this.appStrings.label_lnOrderDetails,
      }
    ];
  }
}
